import { type PathParam, generatePath, matchPath, useParams } from 'react-router';

type ConditionalArgumentOnNoRequiredKeys<T> = keyof T extends never ? [] : [T];

type Params<Pattern extends string> = {
  [key in PathParam<Pattern>]: string;
};

export const createPath =
  <Pattern extends string>(
    pattern: Pattern,
    /**
     * React router 6 requires <Route> inside <Routes> to be relative to the parent <Routes>
     */
    leafRoutesPattern?: string,
  ) =>
  <TypedParams extends Omit<Params<Pattern>, 'pressroomId'>>() => {
    type TParams = Params<Pattern> & TypedParams;
    const paramsToPath = (...[params]: ConditionalArgumentOnNoRequiredKeys<TParams>) => {
      let url = generatePath<Pattern>(pattern, params);
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          const regex = new RegExp(`:${key}(?=&|$)`, 'g');
          url = url.replace(regex, String(value));
        });
        return url;
      }

      return url;
    };

    paramsToPath.pattern = leafRoutesPattern ?? pattern;
    paramsToPath.useParams = function useTypedParams() {
      return useParams() as TParams;
    };

    paramsToPath.matchPath = (path: string) =>
      matchPath<keyof Params<Pattern>, string>({ path: pattern, end: true }, path);

    return paramsToPath;
  };

export default createPath;
